import * as React from 'react';

import { FinanceRootLayout } from '@rematter/modules/finance/_components/_layouts/FinanceRootLayout';
import {
  DataGrid,
  DataGridSelection,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import * as state from '../../_state/financialAccounts.state';

import { FinancialAccountDialog } from './FinancialAccountDialog';
import { FinancialAccountListActions } from './FinancialAccountListActions';
import { FinancialAccountListFilterBar } from './FinancialAccountListFilterBar';
import { FinancialAccountListFloatingToolbar } from './FinancialAccountListFloatingToolbar';
import { useFinancialAccountListColumns } from './hooks/useFinancialAccountListColumns';
import { ReconcileCashDrawerDialog } from './ReconcileCashDrawerDialog';
import { TransactionDrawer } from './TransactionsDrawer';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { FinancialAccountRecord } from '~/modules/finance/types';

//
// ⚛️ ⚛️ ⚛️ FINANCIAL ACCOUNT LIST VIEW ⚛️ ⚛️ ⚛️
//
export const FinancialAccountListView = () => {
  const t = useTranslations();

  // States
  const [selection, setSelection] = React.useState<DataGridSelection>(
    new Set(),
  );
  const [reconcileAccount, setReconcileAccount] = React.useState<
    FinancialAccountRecord | undefined
  >(undefined);

  // Atoms
  const {
    data: { accounts, total } = { accounts: [], total: 0 },
    error,
    refetch,
    isLoading,
  } = useAtomValue(state.$remoteFinancialAccounts);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isEditDialogOpen, setIsEditDialogOpen] = useAtom(
    state.$isEditDialogOpen,
  );
  const [editingAccount, setEditingAccount] = useAtom(state.$editingAccount);
  const [transactionAccount, setTransactionAccount] = useAtom(
    state.$transactionAccount,
  );
  const [isTransactionDrawerOpen, setIsTransactionDrawerOpen] = useAtom(
    state.$isTransactionDrawerOpen,
  );

  // Memoized values
  const selectedRowsCount = React.useMemo(
    () => (selection === 'all' ? accounts.length : selection.size),
    [selection, accounts],
  );

  // Callbacks
  const handleEditAccount = React.useCallback(
    (account: FinancialAccountRecord) => {
      setEditingAccount(account);
      setIsEditDialogOpen(true);
    },
    [setEditingAccount, setIsEditDialogOpen],
  );
  const handleViewTransactions = React.useCallback(
    (account: FinancialAccountRecord) => {
      setTransactionAccount(account);
      setIsTransactionDrawerOpen(true);
    },
    [setTransactionAccount, setIsTransactionDrawerOpen],
  );
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleCloseEditDialog = React.useCallback(() => {
    setIsEditDialogOpen(false);
    setEditingAccount(undefined);
  }, [setIsEditDialogOpen, setEditingAccount]);
  const handleCloseTransactionDrawer = React.useCallback(() => {
    setIsTransactionDrawerOpen(false);
    setTransactionAccount(undefined);
  }, [setIsTransactionDrawerOpen, setTransactionAccount]);
  const handleReconcileAccount = React.useCallback(
    (account: FinancialAccountRecord) => {
      setReconcileAccount(account);
    },
    [],
  );

  const columns = useFinancialAccountListColumns({
    onEditAccount: handleEditAccount,
    onViewTransactions: handleViewTransactions,
    onReconcileAccount: handleReconcileAccount,
  });

  return (
    <FinanceRootLayout className="@FinancialAccountListView">
      <ListScreenLayout
        filters={<FinancialAccountListFilterBar />}
        actions={<FinancialAccountListActions onPlaidSuccess={refetch} />}
        footer={
          (total ?? 0) > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<FinancialAccountRecord>
          selectionMode="multiple"
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          fullWidth
          isLoading={isLoading}
          selectedKeys={selection}
          onSelectionChange={setSelection}
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.financialAccounts.errors.loadingAccounts(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={{
            title: t.financialAccounts.list.empty.description,
            description: t.financialAccounts.list.empty.createPrompt,
          }}
          className="w-full text-sm"
        />
        <FinancialAccountListFloatingToolbar
          selectedRowsCount={selectedRowsCount}
        />
        <FinancialAccountDialog
          isOpen={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          account={editingAccount}
          refetch={refetch}
        />
        <TransactionDrawer
          isOpen={isTransactionDrawerOpen}
          onClose={handleCloseTransactionDrawer}
          account={transactionAccount}
        />
        {reconcileAccount && (
          <ReconcileCashDrawerDialog
            isOpen={!!reconcileAccount}
            onOpenChange={(open) => {
              if (!open) setReconcileAccount(undefined);
            }}
            financialAccountId={reconcileAccount.id}
            currency={reconcileAccount.currency}
            onSuccess={refetch}
          />
        )}
      </ListScreenLayout>
    </FinanceRootLayout>
  );
};
